// src/components/Tenants/Styles/AddTenantDialog.Style.js
// @flow

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as R from 'ramda';
import classNames from 'classnames';
import { log } from '../../utils/jsUtils';
import Downshift from 'downshift';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import StyledPhoneInput from '../SharedComponents/StyledPhoneInput';

import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { createClasses } from '../../utils/stylesUtils';
import styles from './Styles/AddTenantDialog.Style';
import SPBackend from '../../services/SPBackend';
import TagRenderer from '../SharedComponents/TagRenderer';
import AutocompleteProperty from '../Properties/AutocompleteProperty';
import { useI18n } from '../../contexts/I18nContext';
import { selectUser, selectDefaultUnit, selectTags } from '../../reduxStore/modules/user/selectors';
import { selectTenantsPageInfo } from '../../reduxStore/modules/tenants/selectors';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { setLoadingModule } from '../../reduxStore/modules/common/actions';
import { selectAccountSettings } from '../../reduxStore/modules/user/selectors';

import type { Unit } from '../../services/SPBackend';

type Props = {
  handleEditTenant: Function, // if prop missing, component switches in create tenant only mode.
  classes: Object,
  setOpen: Function,
  open: boolean,
  triggerAction: Function,
  unit: Unit
};

const CREATE_NEW_TENANT = 'Create new tenant';

const renderInput = (_inputProps) => {
  const {
    InputProps,
    classes,
    setSelectedAutocompleteVal,
    openMenu,
    ...other
  } = _inputProps;

  setSelectedAutocompleteVal(InputProps.value);
  return (
    <TextField
      InputProps={{
        // inputRef: ref,
        classes: {
          root: classes.inputRoot,
          input: classes.inputInput
        },
        ...InputProps
      }}
      onFocus={openMenu}
      {...other}
    />
  );
};

// eslint-disable-next-line react/no-multi-comp
const SuggestedList = (props) => {
  const {
    inputValue,
    getItemProps,
  } = props;
  
  log('[SuggestedList] inputValue: ', inputValue);
  const value = inputValue.trim().toLowerCase();
  
  // Replace store with Redux hooks
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const tenantsPageInfo = useSelector(selectTenantsPageInfo);
  const i18n = useI18n();

  const [tenants, setTenants] = useState([]);
  const [tenantsNextPage, setTenantsNextPage] = useState(null);
  const [countOfTenants, setCountOfTenants] = useState(0);

  useEffect(() => {
    if (user !== null && value.length >= 0) {
      log('[SPBackend.findTenants]...');
      const findTenantsPayload = {
        search: value,
        tenantsPageInfo
      };
      SPBackend.findTenants(findTenantsPayload, user)
        .then(response => {
          log('[findTenants] response: ', response);
          const { tenants, tenantsNextPage, tenantsCount } = response.data;
          setTenants(tenants);
          setTenantsNextPage(tenantsNextPage);
          setCountOfTenants(tenantsCount);
      })
      .catch(err => {
        log('sentry error: ', err);
      });
    } else {
      setTenants([]);
    }
  }, [inputValue, user, tenantsPageInfo]);


  return (
    !tenants || tenants.length === 0
    ? <MenuItem
        {...getItemProps({item: i18n.t(CREATE_NEW_TENANT)})}
        component='div'
        key={'createTenant'}
      >
        {i18n.t(CREATE_NEW_TENANT)}
      </MenuItem>
    : [
        tenants.map((tenant) => {
          return (
            <MenuItem
              {...getItemProps({item: tenant})}
              component='div'
              key={tenant._id}
            >
              {`${tenant.FirstName} ${tenant.LastName}`}
            </MenuItem>
          );
        }),
        <MenuItem
          {...getItemProps({item: i18n.t(CREATE_NEW_TENANT)})}
          component='div'
          key={'createTenant'}
        >
          {i18n.t(CREATE_NEW_TENANT)}
        </MenuItem>
      ]
  );
};

// eslint-disable-next-line react/no-multi-comp
const AddTenant = (props: Props) => {
  const {
    setOpen,
    open,
    triggerAction,
    handleEditTenant,
    unit
  } = props;

  // Replace store with Redux hooks
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const defaultUnit = useSelector(selectDefaultUnit);
  const tags = useSelector(selectTags);
  const accountSettings = useSelector(selectAccountSettings);
  const i18n = useI18n();
  const theme = useTheme();
  const classes = createClasses(styles, theme);

  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState({
    _id: null,
    FirstName: '',
    LastName: '',
    Phone: '',
    Email: '',
    Tags: []
  });
  const [newTenant, setNewTenant] = useState({
    FirstName: '',
    LastName: '',
    Phone: '',
    Email: '',
    // Keys: defaultUnit ? [defaultUnit.RoomKey] : unit ? [unit.RoomKey] : [],
    Tags: []
  });

  const [selectedAutocompleteVal, setSelectedAutocompleteVal] = useState(null);
  const [showCreateTenantForm, setShowCreateTenantForm] = useState(false);


  useEffect(() => {
    log('[effect.selectedAutocompleteVal]: ', selectedAutocompleteVal);
    if (selectedAutocompleteVal === (i18n.t(CREATE_NEW_TENANT))
      || typeof handleEditTenant === 'undefined') {
      setShowCreateTenantForm(true);
    } else {
      setShowCreateTenantForm(false);
    }
  }, [selectedAutocompleteVal, i18n, handleEditTenant]);

  useEffect(() => {
    // Fix infinite loop by removing newTenant from dependencies
    // Only update when unit or defaultUnit changes
    setNewTenant(prev => ({
      ...prev
      // Keys: defaultUnit ? [defaultUnit.RoomKey] : unit ? [unit.RoomKey] : []
    }));
  }, [unit, defaultUnit]);

  const emptyForm = () => {
    setNewTenant(prev => ({
      ...prev,
      FirstName: '',
      LastName: '',
      Phone: '',
      Email: '',
      Tags: []
    }));
  };

  const callbackOnTagPicked = (params) => {
    log('[callbackOnTagPicked] params: ', params);
    setNewTenant(prev => ({
      ...prev,
      Tags: R.append(params._id, prev.Tags)
    }));
  };

  return (
    <Dialog aria-labelledby='form-add-unit'
      onClose={() => {setOpen(false); emptyForm();}}
      open={open}
    >
      <form
        autoComplete='off'
        style={{
          ...classes.dialogFormCtr,
          ...(!unit ? classes.xlarge : showCreateTenantForm ? classes.large : classes.normal)
        }}
        noValidate
        onSubmit={e => {
          e.preventDefault();
          setLoading(true);
          triggerAction(newTenant,
            () => {
              setLoading(false);
              setOpen(false);
              emptyForm();
            },
            () => {
              setLoading(false);
            }
          );
        }}
      >
        <DialogTitle id='form-dialog-title'>{`Add a ${i18n.t('tenant')}`}</DialogTitle>
        <DialogContent sx={{...classes.grow, ...classes.flexContainer}}>
          {
            typeof handleEditTenant !== 'undefined'
            ? <Box id='searchTenant'>
              <Downshift 
                id='downshift-simple'
                itemToString={item => item && item !== (i18n.t(CREATE_NEW_TENANT)) ? 
                `${item.FirstName} ${item.LastName}` : i18n.t(CREATE_NEW_TENANT)}
                onSelect={(selectedTenant) => setTenant(selectedTenant)}
              >
                {({
                  getInputProps,
                  getItemProps,
                  getMenuProps,
                  highlightedIndex,
                  inputValue,
                  isOpen,
                  openMenu
                }) => (
                  <Box sx={classes.container}>
                    {renderInput({
                      ref: 'test',
                      fullWidth: true,
                      classes,
                      InputProps: getInputProps({
                        placeholder: `Search ${i18n.t('a tenant')}`
                      }),
                      openMenu,
                      setSelectedAutocompleteVal
                    })}
                    <Box {...getMenuProps()}>
                      {isOpen ? (
                        <Paper
                          sx={classes.paper}
                          square
                        >
                          <SuggestedList
                            getItemProps={getItemProps}
                            inputValue={inputValue}
                          />
                        </Paper>
                      ) : null}
                    </Box>
                  </Box>
                )}
              </Downshift>
            </Box>
            : null
          }
          {showCreateTenantForm ? (
            <Box id='createTenant'>
              <Input
                autoFocus
                sx={classes.secondInput}
                fullWidth
                id='firstName'
                label='First Name'
                margin='dense'
                onChange={e =>
                  setNewTenant(prev => ({
                    ...prev,
                    FirstName: e.target.value
                  }))
                }
                placeholder='First name'
                type='text'
                value={newTenant.FirstName}
              />
              <Input
                sx={classes.secondInput}
                fullWidth
                id='lastName'
                label='Last Name'
                margin='dense'
                onChange={e =>
                  setNewTenant(prev => ({
                    ...prev,
                    LastName: e.target.value
                  }))
                }
                placeholder='Last name'
                type='text'
                value={newTenant.LastName}
              />
              <Input
                sx={classes.secondInput}
                fullWidth
                id='email'
                label='Email'
                margin='dense'
                onChange={e =>
                  setNewTenant(prev => ({
                    ...prev,
                    Email: e.target.value
                  }))
                }
                placeholder='Email'
                type='email'
                value={newTenant.Email}
              />
              <Box sx={{ marginTop: 2 }}>
                <StyledPhoneInput
                  defaultCountry='US'
                  onChange={e =>
                    setNewTenant(prev => ({
                      ...prev,
                      Phone: e
                    }))
                  }
                  placeholder="Enter phone number"
                  value={newTenant.Phone}
                />
              </Box>

              <TagRenderer
                callbackOnTagPicked={callbackOnTagPicked}
                tagsIds={newTenant.Tags}
              />

              {
                !defaultUnit && !unit
                ? <Box sx={classes.secondInput}>
                  <AutocompleteProperty 
                    setUnit={(unit:Unit)=> {
                      setNewTenant(prev => ({
                        ...prev
                        // Keys: [unit.RoomKey]
                      }));
                    }}
                  />
                </Box>
                : null
              }
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            color='primary'
            onClick={() => {setOpen(false); emptyForm();}}
          >
            {'Cancel'}
          </Button>
          
          {
            showCreateTenantForm 
            ? <Button
                color='primary'
                disabled={loading}
                type='submit'
                variant='contained'
              >
                {`Add ${i18n.t('tenant')}`}
              </Button>
            : <Button
                color='primary'
                onClick={() => {setOpen(false); handleEditTenant(tenant); emptyForm();}}
                variant='contained'
              >
                {`Assign ${i18n.t('tenant')}`}
              </Button>
          }
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddTenant;
